import { useEffect, useRef } from "react";

interface UseJitsiMeetOptions {
  domain: string;
  roomName: string;
  jwt: string;
  displayName: string;
  containerRef: React.RefObject<HTMLDivElement>;
  onCallEnded?: () => void;
}

declare global {
  interface Window {
    JitsiMeetExternalAPI: any;
  }
}

export function useJitsiMeet({
  domain,
  roomName,
  jwt,
  displayName,
  containerRef,
  onCallEnded,
}: UseJitsiMeetOptions) {
  const apiRef = useRef<any>(null);

  useEffect(() => {
    if (!domain || !roomName || !jwt || !containerRef.current) return;

    const scriptId = "jitsi-external-api";
    const existingScript = document.getElementById(scriptId);

    const initJitsi = () => {
      if (!containerRef.current) return;
      apiRef.current = new window.JitsiMeetExternalAPI(domain, {
        roomName,
        jwt,
        parentNode: containerRef.current,
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          defaultLanguage: "ro",
        },
        interfaceConfigOverwrite: {
          TOOLBAR_BUTTONS: [
            "microphone",
            "camera",
            "closedcaptions",
            "desktop",
            "fullscreen",
            "fodeviceselection",
            "hangup",
            "chat",
            "tileview",
          ],
        },
        userInfo: {
          displayName,
        },
      });

      apiRef.current.addEventListeners({
        readyToClose: () => {
          if (onCallEnded) onCallEnded();
        },
        videoConferenceLeft: () => {
          if (onCallEnded) onCallEnded();
        },
      });
    };

    if (existingScript) {
      if (window.JitsiMeetExternalAPI) {
        initJitsi();
      } else {
        existingScript.addEventListener("load", initJitsi);
      }
    } else {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://${domain}/external_api.js`;
      script.async = true;
      script.onload = initJitsi;
      document.body.appendChild(script);
    }

    return () => {
      if (apiRef.current) {
        apiRef.current.dispose();
        apiRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, roomName, jwt]);

  const hangUp = () => {
    if (apiRef.current) {
      apiRef.current.executeCommand("hangup");
    }
  };

  return { hangUp };
}
